import { Trans } from '@lingui/react'
import { RxByPrincipleReasonSet } from '@redteclab/api/clients/bully'
import { bool, number, oneOf, shape, string } from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'

import { DOCUMENT_TYPE } from 'shared/consts'
import { AVAILABLE_PRESCRIPTION_TYPES } from 'shared/consts/productTemplates'
import { isSaeOnly } from 'shared/experiments/utils/featureFlags'
import {
  extractHostAndPathFromTrackingUrl,
  extractJParamFromTrackingUrl,
} from 'shared/utils/adServerUtils'
import { RX_BY_PRINCIPLE_REASON_INPUT_ID } from 'views/assets/scripts/rxByPrincipleModal'
import Form from 'views/components/atoms/Form'
import IconCart from 'views/components/atoms/Icon/IconCart'
import ButtonWithIcon from 'views/components/molecules/ButtonWithIcon'
import RxByPrincipleModal from 'views/components/organisms/RxByPrincipleModal'
import withMountPoints from 'views/enhancers/withMountPoints'
import buildClientAPI from 'views/providers/clientAPI'
import { isProductAvailable } from 'views/utils/productUtils'
import { mountPointsType, StockStatusType } from 'views/utils/propTypes'
import {
  usePageProperties,
  usePublicConfig,
} from 'views/utils/shopAndRuntimeConfigUtils'

let offerIdsWithAlreadySelectedReasons
let cartMetaDataLoading = false

/**
 * @returns {Promise<string[]>}
 */
const fetchOfferIdsWithAlreadySelectedReasons = async () => {
  const { cartService } = buildClientAPI()

  try {
    const { data } = await cartService.fetchMetadata()

    return data.rxByPrinciple?.offerIdsWithAlreadySelectedReasons ?? []
  } catch {
    return []
  }
}

/**
 * @param {string} offerId
 * @param {string[]} offerIdsWithAlreadySelectedReasons
 */
const isReasonAlreadySelected = (offerId, offerIdsWithAlreadySelectedReasons) =>
  offerIdsWithAlreadySelectedReasons.includes(offerId)

const CartForm = ({
  algoliaQueryId,
  mobile,
  mountPoints,
  offerId,
  position,
  prescriptionType,
  product,
  productId,
  stockStatus,
  trackingUrls,
}) => {
  const isAvailable = isProductAvailable(stockStatus)

  const { tenant } = usePublicConfig()
  const { experiments, shopOrigin } = usePageProperties()

  const queryParams = {
    prescriptionType,
    position,
    product: productId,
    offer: offerId,
    cartAction: 'add',
    eventType: 'conversion',
    eventName: 'addToCart_ProductFilter',
    quantity: '1',
  }

  if (algoliaQueryId) {
    queryParams.queryID = algoliaQueryId
  }

  if (queryParams.position) {
    queryParams.position = position
  }

  if (isSaeOnly(experiments, tenant)) {
    queryParams.offer = offerId
  }

  let action = mountPoints.getAddToCart()

  if (trackingUrls?.clickUrl) {
    const clickUrl = trackingUrls.clickUrl

    const jParam = extractJParamFromTrackingUrl(clickUrl)
    const adServerApiUrl = extractHostAndPathFromTrackingUrl(clickUrl)

    const cartIntermediateRedirectUrl =
      shopOrigin + mountPoints.getAddToCart(queryParams)

    action = adServerApiUrl || mountPoints.getAddToCart()

    if (jParam) {
      queryParams.j = jParam
    }

    if (cartIntermediateRedirectUrl) {
      queryParams.url = cartIntermediateRedirectUrl
    }
  }

  const reasonSet = product.rx_by_principle?.reason_set
  const [rxByPrincipleModalOpen, setRxByPrincipleModalOpen] = useState(false)
  const [selectedReason, setSelectedReason] = useState()

  const onSubmit = useCallback(
    event => {
      const form = event.currentTarget

      if (!reasonSet || selectedReason) {
        return
      }

      if (cartMetaDataLoading) {
        event.preventDefault()

        return
      }

      if (!offerIdsWithAlreadySelectedReasons) {
        cartMetaDataLoading = true

        event.preventDefault()

        /**
         * There is no good tooling to fetch data dynamically in hydrated components.
         * Data is fetched on submit ang could cause state change when component is unmounted.
         * For Fock it's ok as the page will be reloaded after submission and no unmount happens.
         */
        void fetchOfferIdsWithAlreadySelectedReasons().then(
          offerIdsWithAlreadySelectedReasons => {
            cartMetaDataLoading = false

            if (
              isReasonAlreadySelected(
                offerId,
                offerIdsWithAlreadySelectedReasons
              )
            ) {
              form.submit()
            } else {
              setRxByPrincipleModalOpen(true)
            }
          }
        )

        return
      } else if (
        isReasonAlreadySelected(offerId, offerIdsWithAlreadySelectedReasons)
      ) {
        return
      }

      event.preventDefault()

      setRxByPrincipleModalOpen(true)
    },
    [offerId, reasonSet, selectedReason]
  )

  const onReasonSelect = useCallback(reason => {
    setRxByPrincipleModalOpen(false)
    setSelectedReason(reason)
  }, [])

  const formRef = useRef(null)

  useEffect(() => {
    if (selectedReason) {
      formRef.current?.formRef.current.submit()
    }
  }, [selectedReason])

  return (
    <>
      <Form
        name={`buy-product-from-${productId}-${mobile ? 'mobile' : 'laptop'}`}
        action={action}
        data-clientside-hook="chargeUrlSearchParams add2CartForm"
        data-qa-id="add-to-cart-button"
        onSubmit={onSubmit}
        ref={formRef}
      >
        {Object.entries(queryParams).map(([name, value]) => (
          <input type="hidden" key={name} name={name} value={value} />
        ))}
        {selectedReason && (
          <input
            id={RX_BY_PRINCIPLE_REASON_INPUT_ID}
            name={`rxByPrincipleReason[${offerId}]`}
            type="hidden"
            value={selectedReason}
          />
        )}
        <ButtonWithIcon
          name={`buy-product-${productId}-${mobile ? 'mobile' : 'laptop'}`}
          disabled={!isAvailable}
          type="submit"
          icon={<IconCart />}
          component="Button"
        >
          <Trans id="general.button.label.addToCart" />
        </ButtonWithIcon>
      </Form>
      {reasonSet && (
        <RxByPrincipleModal
          isClientSideControlled
          onHideModal={() => {
            setRxByPrincipleModalOpen(false)
          }}
          onReasonSelect={onReasonSelect}
          open={rxByPrincipleModalOpen}
          productName={product.name}
          reasonSet={reasonSet}
        />
      )}
    </>
  )
}

CartForm.propTypes = {
  algoliaQueryId: string,
  mobile: bool,
  mountPoints: mountPointsType.isRequired,
  offerId: string,
  position: number,
  prescriptionType: oneOf(AVAILABLE_PRESCRIPTION_TYPES.ALL),
  product: shape({
    name: string.isRequired,
    rx_by_principle: shape({
      reason_set: oneOf(Object.values(RxByPrincipleReasonSet)),
    }),
  }).isRequired,
  productId: string.isRequired,
  stockStatus: shape(StockStatusType).isRequired,
  trackingUrls: shape({}),
}

CartForm.defaultProps = {
  algoliaQueryId: null,
  mobile: false,
  offerId: null,
  position: null,
  prescriptionType: DOCUMENT_TYPE.NO_PRESCRIPTION,
  trackingUrls: null,
}

export default withMountPoints(CartForm)
